import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { tasksAPI } from '../services/api';
import { Task } from '../types';
import TaskDetailSidebar from './TaskDetailSidebar';
import TaskModal from './TaskModal';

interface ProjectTasksTimelineProps {
  projectId: string;
}

const ProjectTasksTimeline: React.FC<ProjectTasksTimelineProps> = ({ projectId }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    loadTasks();
  }, [projectId]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await tasksAPI.getAll({ projectId });
      const tasksList = Array.isArray(data) ? data : (data as any).items || [];
      setTasks(tasksList);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setTaskModalVisible(true);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setTaskModalVisible(true);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = firstDay.getDate(); i <= lastDay.getDate(); i++) {
      days.push(i);
    }
    return days;
  };

  const getTasksForDay = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return tasks.filter(task => {
      if (!task.deadline) return false;
      const taskDate = new Date(task.deadline);
      return taskDate.getDate() === day &&
             taskDate.getMonth() === currentMonth.getMonth() &&
             taskDate.getFullYear() === currentMonth.getFullYear();
    });
  };

  const getTasksForStatus = (status: string) => {
    return tasks.filter(task => task.status === status);
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('sr-RS', { month: 'long', year: 'numeric' });

  const statusConfig: Record<string, { label: string; color: string }> = {
    todo: { label: 'Za rad', color: '#3b82f6' },
    in_progress: { label: 'U toku', color: '#f59e0b' },
    done: { label: 'Završeno', color: '#10b981' }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentMonth);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentMonth(newDate);
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button
            icon="pi pi-chevron-left"
            className="p-button-text"
            onClick={() => navigateMonth('prev')}
          />
          <h2 style={{ margin: 0, minWidth: '200px', textAlign: 'center' }}>{monthName}</h2>
          <Button
            icon="pi pi-chevron-right"
            className="p-button-text"
            onClick={() => navigateMonth('next')}
          />
        </div>
        <Button
          label="Add task"
          icon="pi pi-plus"
          className="p-button-success"
          onClick={handleAddTask}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '800px' }}>
          {/* Header with days */}
          <div style={{ display: 'grid', gridTemplateColumns: '150px repeat(31, 40px)', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div style={{ fontWeight: 'bold', padding: '0.5rem' }}>Zadatak</div>
            {days.map(day => (
              <div
                key={day}
                style={{
                  textAlign: 'center',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #e5e7eb'
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Task rows by status */}
          {Object.keys(statusConfig).map(status => {
            const statusTasks = getTasksForStatus(status);
            const config = statusConfig[status];
            
            return (
              <div key={status} style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: config.color }}>
                  {config.label}
                </div>
                {statusTasks.map(task => {
                  const taskStartDay = task.deadline ? new Date(task.deadline).getDate() : null;
                  const taskEndDay = taskStartDay; // For simplicity, tasks span one day
                  
                  return (
                    <div
                      key={task._id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '150px repeat(31, 40px)',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleTaskClick(task)}
                    >
                      <div style={{ padding: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                        {task.name}
                      </div>
                      {days.map(day => {
                        if (taskStartDay && day >= taskStartDay && day <= (taskEndDay || taskStartDay)) {
                          return (
                            <div
                              key={day}
                              style={{
                                backgroundColor: config.color,
                                borderRadius: '4px',
                                padding: '0.25rem',
                                color: 'white',
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                cursor: 'pointer'
                              }}
                              title={task.name}
                            />
                          );
                        }
                        return <div key={day} />;
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {selectedTask && (
        <TaskDetailSidebar
          task={selectedTask}
          visible={!!selectedTask}
          onHide={() => setSelectedTask(null)}
          onTaskUpdate={loadTasks}
        />
      )}

      {taskModalVisible && (
        <TaskModal
          visible={taskModalVisible}
          onHide={() => {
            setTaskModalVisible(false);
            setEditingTask(null);
          }}
          projectId={projectId}
          task={editingTask}
          onSave={loadTasks}
        />
      )}
    </div>
  );
};

export default ProjectTasksTimeline;
